import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  CheckCircle2, 
  Users, 
  Ticket, 
  Store, 
  BarChart3,
  Shield,
  Heart,
  Briefcase,
  Menu,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: Ticket,
      title: "Voucher Management",
      description: "Issue, track, and manage clothing vouchers for job seekers with full transparency and accountability."
    },
    {
      icon: Users,
      title: "Beneficiary Tracking",
      description: "Register and verify eligible participants with comprehensive eligibility assessment workflows."
    },
    {
      icon: Store,
      title: "Partner Network",
      description: "Connect with retail partners who provide quality interview attire at approved locations."
    },
    {
      icon: BarChart3,
      title: "Impact Reporting",
      description: "Generate detailed reports for stakeholders showcasing program outcomes and ROI."
    }
  ];

  const stats = [
    { value: "5,000+", label: "Job Seekers Served" },
    { value: "TBD", label: "Vouchers Distributed" },
    { value: "150+", label: "Partner Retailers" },
    { value: "78%", label: "Employment Success Rate" }
  ];

  const benefits = [
    "Restore dignity and confidence for job interviews",
    "Remove barriers to employment for underserved communities",
    "Support local businesses through partner retail network",
    "Ensure accountability with transparent tracking systems",
    "Meet government compliance and reporting requirements",
    "Scale programs efficiently with digital voucher management"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-900 to-amber-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold">SV</span>
              </div>
              <span className="font-semibold text-slate-900 text-lg hidden sm:block">Soar Your Visibility</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#impact" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Impact</a>
              <Link to="/business-plan" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Business Plan</Link>
              <Link to="/get-help" data-testid="nav-get-help" className="text-sm font-medium text-amber-700 hover:text-amber-800 transition-colors">Get Help</Link>
              <Link to="/donate" data-testid="nav-donate">
                <Button variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50 rounded-md px-5 gap-1.5">
                  <Heart className="w-4 h-4" /> Donate
                </Button>
              </Link>
              <Link to="/login" data-testid="nav-get-started">
                <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-6">
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-slate-600"
              data-testid="mobile-nav-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-100">
              <div className="flex flex-col gap-3">
                <a href="#features" className="text-sm font-medium text-slate-600 py-2">Features</a>
                <a href="#impact" className="text-sm font-medium text-slate-600 py-2">Impact</a>
                <Link to="/business-plan" className="text-sm font-medium text-slate-600 py-2">Business Plan</Link>
                <Link to="/get-help" data-testid="mobile-get-help" className="text-sm font-medium text-amber-700 py-2">Get Help</Link>
                <Link to="/donate" data-testid="mobile-donate" className="text-sm font-medium text-amber-700 py-2">Donate</Link>
                <Link to="/login" data-testid="mobile-get-started">
                  <Button className="w-full bg-slate-900 text-white hover:bg-slate-800 mt-2">
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium">
                <Shield className="w-4 h-4" />
                Government Contract Ready
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Empowering Job Seekers with{" "}
                <span className="text-amber-600">Professional Attire</span>
              </h1>
              
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
                Soar Your Visibility provides a comprehensive voucher program management system 
                for government agencies and nonprofits helping unemployed and underserved 
                individuals secure interview-appropriate clothing.
              </p>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                  <Link to="/login" data-testid="hero-cta-dashboard">
                    <Button className="bg-amber-600 text-white hover:bg-amber-700 rounded-md px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all">
                      Access Dashboard
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/business-plan" data-testid="hero-cta-business-plan">
                    <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md px-8 py-6 text-lg">
                      View Business Plan
                    </Button>
                  </Link>
                  <Link to="/get-help" data-testid="hero-cta-get-help">
                    <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-md px-8 py-6 text-lg">
                      <Heart className="w-5 h-5 mr-2" />
                      I Need Help
                    </Button>
                  </Link>
                </div>
                <Link to="/donate" data-testid="hero-cta-donate" className="sm:self-start">
                  <Button className="w-full sm:w-auto bg-slate-900 text-white hover:bg-slate-800 rounded-md px-8 py-6 text-lg gap-2 shadow-lg">
                    <Heart className="w-5 h-5" /> Donate Now
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-5 animate-fade-in-up animate-delay-200">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-sky-400/20 rounded-3xl transform rotate-3"></div>
                <img 
                  src="https://images.pexels.com/photos/5439143/pexels-photo-5439143.jpeg"
                  alt="Diverse group of professionals looking confident"
                  className="relative rounded-3xl shadow-2xl w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.label} 
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-3xl sm:text-4xl font-bold text-amber-500 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {stat.value}
                </div>
                <div className="text-slate-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Complete Program Management
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to run an effective clothing voucher program for workforce development.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={feature.title}
                className="bg-white rounded-xl border border-slate-100 shadow-xl shadow-slate-200/50 p-8 hover:border-amber-200 transition-all hover:-translate-y-1"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-0">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                    <feature.icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section id="impact" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/32549954/pexels-photo-32549954.jpeg"
                alt="Modern clothing retail store interior"
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-6 hidden lg:block">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Heart className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">92%</div>
                    <div className="text-sm text-slate-500">Client Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Making a Measurable Impact on Employment
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Our program addresses a critical barrier to employment: appropriate interview attire. 
                By providing dignified access to professional clothing, we help job seekers present 
                their best selves and increase their chances of success.
              </p>

              <div className="space-y-4">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <Link to="/business-plan" data-testid="impact-cta">
                <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-6 py-3 font-medium mt-4">
                  Read Full Business Plan
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-8">
            <Briefcase className="w-4 h-4" />
            Ready to Get Started?
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Partner with Soar Your Visibility
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Whether you're a government agency, nonprofit organization, or retail partner, 
            we're ready to help you make a difference in your community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login" data-testid="cta-dashboard">
              <Button className="bg-amber-600 text-white hover:bg-amber-700 rounded-md px-8 py-6 text-lg font-semibold">
                Launch Dashboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/business-plan" data-testid="cta-business-plan">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-md px-8 py-6 text-lg">
                Download Business Plan
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-slate-700 to-amber-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">SV</span>
              </div>
              <span className="text-slate-400">Soar Your Visibility</span>
            </div>
            <div className="flex items-center gap-8 text-sm text-slate-500">
              <Link to="/business-plan" className="hover:text-slate-300 transition-colors">Business Plan</Link>
              <Link to="/dashboard" className="hover:text-slate-300 transition-colors">Dashboard</Link>
              <span>© 2026 All rights reserved</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
